import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Loader2, AlertTriangle, Plus, X, TrendingUp, ShieldAlert, MessageSquareQuote,
  ChevronDown, ChevronUp, Sparkles, Info, ClipboardList, ArrowRight, CheckCircle2, ExternalLink,
  Flag, Copy, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, addRepKey, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/* ---------------- data-label + priority styling ---------------- */
const LABEL_STYLE = {
  VERIFIED_SOURCE_DATA: { t: "Verified source", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  DIRECT_API_DATA: { t: "Direct API data", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  SEARCH_DISCOVERED_SOURCE: { t: "Search-discovered", c: "bg-sky-50 text-sky-700 border-sky-200" },
  SOURCE_PAGE_DATA: { t: "Source-page data", c: "bg-teal-50 text-teal-700 border-teal-200" },
  CORROBORATED_DATA: { t: "Corroborated", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  CUSTOMER_CLAIM: { t: "Customer claim", c: "bg-sky-50 text-sky-700 border-sky-200" },
  AI_ANALYZED_THEME: { t: "AI-analysed", c: "bg-violet-50 text-violet-700 border-violet-200" },
  AI_ANALYZED_FROM_SOURCE_EVIDENCE: { t: "AI-analysed from source", c: "bg-violet-50 text-violet-700 border-violet-200" },
  BUSINESS_RECOMMENDATION: { t: "Recommendation", c: "bg-amber-50 text-amber-700 border-amber-200" },
  MODELED_FINANCIAL_OPPORTUNITY: { t: "Modeled estimate", c: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  UNABLE_TO_VERIFY: { t: "Unable to verify", c: "bg-slate-100 text-slate-500 border-slate-200" },
  NOT_CONFIGURED: { t: "Not connected", c: "bg-slate-100 text-slate-500 border-slate-200" },
  NOT_FOUND: { t: "Not found", c: "bg-slate-100 text-slate-500 border-slate-200" },
  ASSUMPTION: { t: "Assumption", c: "bg-slate-100 text-slate-500 border-slate-200" },
};
const PRIO = {
  Critical: "bg-red-100 text-red-700 border-red-200",
  High: "bg-orange-100 text-orange-700 border-orange-200",
  Medium: "bg-amber-100 text-amber-700 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
  None: "bg-slate-100 text-slate-500 border-slate-200",
};
const PRIO_BAR = { Critical: "bg-red-500", High: "bg-orange-500", Medium: "bg-amber-500", Low: "bg-slate-400", None: "bg-slate-300" };

function Label({ label, testid }) {
  if (!label) return null;
  const s = LABEL_STYLE[label] || LABEL_STYLE.ASSUMPTION;
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.c}`}>
      {s.t}
    </span>
  );
}

/* ---------------- review paste repeater ---------------- */
function ReviewRow({ row, i, onChange, onRemove }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`paste-review-${i}`}>
      <div className="flex items-center gap-2">
        <select value={row.rating} onChange={(e) => onChange(i, { ...row, rating: e.target.value })}
          data-testid={`paste-rating-${i}`}
          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700">
          <option value="">Rating</option>
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
        </select>
        <input value={row.author} onChange={(e) => onChange(i, { ...row, author: e.target.value })}
          placeholder="Reviewer (optional)" data-testid={`paste-author-${i}`}
          className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" />
        <button onClick={() => onRemove(i)} data-testid={`paste-remove-${i}`}
          className="grid place-items-center w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50">
          <X className="w-4 h-4" />
        </button>
      </div>
      <textarea value={row.text} onChange={(e) => onChange(i, { ...row, text: e.target.value })}
        placeholder="Paste the review text here…" rows={2} data-testid={`paste-text-${i}`}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-y" />
    </div>
  );
}

/* ---------------- recovery index gauge ---------------- */
function RecoveryIndex({ index }) {
  if (!index) return null;
  const level = index.level || "None";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="recovery-index">
      <div className="flex items-start gap-4">
        <div className={`grid place-items-center w-20 h-20 rounded-2xl border-2 shrink-0 ${PRIO[level]}`}>
          <span className="text-2xl font-extrabold leading-none" data-testid="recovery-index-score">{index.score}</span>
          <span className="text-[9px] font-bold uppercase tracking-wide mt-0.5">/ 100</span>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-lg font-bold text-slate-900">Reputation Recovery Priority</h2>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${PRIO[level]}`}>{level}</span>
            <Label label={index.data_label} />
          </div>
          <p className="text-[13px] text-slate-500 mt-1">{index.note}</p>
        </div>
      </div>
      {(index.components || []).length > 0 && (
        <div className="mt-4 space-y-2" data-testid="recovery-index-breakdown">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Why this score</p>
          {index.components.map((c, i) => (
            <div key={i} className="flex items-center gap-3 text-[12px]">
              <span className="w-40 shrink-0 text-slate-600">{c.factor}</span>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-indigo-500" style={{ width: `${Math.min(c.contribution / (c.weight * 100) * 100, 100)}%` }} />
              </div>
              <span className="w-24 text-right text-slate-500">{c.value}</span>
              <span className="w-10 text-right font-semibold text-slate-700">+{c.contribution}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- theme card ---------------- */
function ThemeCard({ theme, i, onAddTask, onDraft, added }) {
  const [open, setOpen] = useState(false);
  const level = theme.priority?.level || "Medium";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`theme-card-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900" data-testid={`theme-label-${i}`}>{theme.label}</h3>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${PRIO[level]}`} data-testid={`theme-priority-${i}`}>
              {level} · {theme.priority?.score}
            </span>
            <Label label={theme.data_label} />
          </div>
          <p className="text-[13px] text-slate-600 mt-1">{theme.summary}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-slate-900">{theme.frequency_pct}%</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">{theme.mentions} mention(s)</div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-[12px]">
        <span className="text-slate-400 uppercase tracking-wide text-[10px] font-semibold w-16">Severity</span>
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden max-w-[220px]">
          <div className={`h-full ${PRIO_BAR[level]}`} style={{ width: `${Math.round((theme.severity || 0) * 100)}%` }} />
        </div>
        <span className="text-slate-500">{Math.round((theme.severity || 0) * 100)}%</span>
      </div>

      <div className="mt-3 rounded-xl bg-amber-50/60 border border-amber-100 p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Recommended fix</span>
          <Label label={theme.action_data_label} />
        </div>
        <p className="text-[13px] text-slate-700" data-testid={`theme-action-${i}`}>{theme.recommended_action}</p>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => onAddTask(theme)} disabled={added}
          data-testid={`theme-add-task-${i}`}
          className={added ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-900 hover:bg-slate-800"}>
          {added ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Added to Fix Plan</> : <><Plus className="w-4 h-4 mr-1.5" /> Add to Fix Plan</>}
        </Button>
        <button onClick={() => setOpen((v) => !v)} data-testid={`theme-toggle-${i}`}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-800">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Score breakdown & evidence
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden">
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              <div className="space-y-1.5" data-testid={`theme-breakdown-${i}`}>
                {(theme.priority?.breakdown || []).map((b, k) => (
                  <div key={k} className="flex items-center gap-2 text-[12px]">
                    <span className="w-44 shrink-0 text-slate-600">{b.factor}</span>
                    <span className="flex-1 text-slate-400">{b.value}</span>
                    <span className="text-slate-500">weight {Math.round(b.weight * 100)}%</span>
                    <span className="w-10 text-right font-semibold text-slate-700">+{b.contribution}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Evidence from reviews</p>
                {(theme.examples || []).map((e, k) => (
                  <div key={k} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[11px] font-medium text-slate-500">{e.author || "Anonymous"}{e.rating ? ` · ${e.rating}★` : ""} · {e.source}</span>
                      <Label label={e.verification_status} />
                    </div>
                    <p className="text-[12px] text-slate-600 italic">“{e.text}”</p>
                    <button onClick={() => onDraft(e)} data-testid={`theme-draft-${i}-${k}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline">
                      <MessageSquareQuote className="w-3.5 h-3.5" /> Draft a response
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------- response assistant modal ---------------- */
function ResponseModal({ ctx, biz, onClose }) {
  const [tone, setTone] = useState("professional");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);

  const gen = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await api.post("/reputation/respond", {
        name: biz.name, city: biz.city, state: biz.state, category: biz.category,
        tone: t, review: { author: ctx.author, rating: ctx.rating ? Number(ctx.rating) : null, text: ctx.text },
      });
      setDraft(data.draft || "");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  }, [biz, ctx]);

  useEffect(() => { gen(tone); }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-900/50 p-4" data-testid="response-modal" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><MessageSquareQuote className="w-5 h-5 text-indigo-600" /> Review Response Assistant</h3>
          <button onClick={onClose} data-testid="response-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mb-3">
          <p className="text-[11px] text-slate-500 mb-1">{ctx.author || "Anonymous"}{ctx.rating ? ` · ${ctx.rating}★` : ""}</p>
          <p className="text-[13px] text-slate-600 italic">“{ctx.text}”</p>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[12px] text-slate-500">Tone:</span>
          {["professional", "warm", "concise", "apologetic"].map((t) => (
            <button key={t} onClick={() => { setTone(t); gen(t); }} data-testid={`response-tone-${t}`}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium border ${tone === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="relative">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={6} data-testid="response-draft"
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm resize-y" />
          {loading && <div className="absolute inset-0 grid place-items-center bg-white/60 rounded-xl"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Label label="BUSINESS_RECOMMENDATION" />
          <span className="text-[11px] text-slate-400">You control publishing — review before posting.</span>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => gen(tone)} data-testid="response-regenerate"><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate</Button>
          <Button size="sm" onClick={() => { navigator.clipboard?.writeText(draft); toast.success("Copied to clipboard"); }} data-testid="response-copy">
            <Copy className="w-4 h-4 mr-1.5" /> Copy
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

/* ---------------- main page ---------------- */
export default function ReputationRecovery() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [biz, setBiz] = useState({
    name: sp.get("name") || "", city: sp.get("city") || "", state: sp.get("state") || "",
    category: sp.get("category") || "", website_url: sp.get("website") || "", business_key: sp.get("key") || "",
  });
  const [mode, setMode] = useState("paste");
  const [rows, setRows] = useState([{ author: "", rating: "", text: "" }, { author: "", rating: "", text: "" }, { author: "", rating: "", text: "" }]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [added, setAdded] = useState({});
  const [respCtx, setRespCtx] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async (key) => {
    if (!key) return;
    try { const { data } = await api.get(`/reputation/history/${encodeURIComponent(key)}`); setHistory(data.history || []); }
    catch (e) { /* noop */ }
  }, []);

  const runAnalyze = async () => {
    if (!biz.name.trim()) { toast.error("Enter the business name first."); return; }
    setLoading(true); setAnalysis(null);
    const payload = { name: biz.name, city: biz.city, state: biz.state, category: biz.category, website_url: biz.website_url, business_key: biz.business_key || undefined };
    if (mode === "paste") {
      const reviews = rows.filter((r) => (r.text || "").trim()).map((r) => ({ author: r.author || null, rating: r.rating ? Number(r.rating) : null, text: r.text }));
      if (!reviews.length) { toast.error("Paste at least one review to analyse."); setLoading(false); return; }
      payload.reviews = reviews;
    }
    try {
      const { data } = await api.post("/reputation/analyze", payload);
      setAnalysis(data);
      setAdded({});
      if (data.business_key) { addRepKey(data.business_key); loadHistory(data.business_key); }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  const addTask = async (theme) => {
    const task = (analysis.tasks || []).find((t) => t.code === `rep_${theme.key}`);
    if (!task) return;
    try {
      await api.post("/tasks", task);
      setAdded((a) => ({ ...a, [theme.key]: true }));
      if (analysis.business_key) addRepKey(analysis.business_key);
      toast.success(`Added “${theme.label}” to your Fix Plan`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const setRow = (i, v) => setRows((rs) => rs.map((r, k) => (k === i ? v : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, k) => k !== i));
  const m = analysis?.metrics || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white shrink-0"><Star className="w-5 h-5" /></span>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Reputation Recovery</h1>
            <p className="text-slate-500 text-sm">Turn real customer reviews into recurring-complaint themes, prioritised fixes and professional responses — evidence-based, never fabricated.</p>
          </div>
        </div>

        {/* Business + source form */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="reputation-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <input value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} placeholder="Business name" data-testid="rep-input-name" className="col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={biz.city} onChange={(e) => setBiz({ ...biz, city: e.target.value })} placeholder="City" data-testid="rep-input-city" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={biz.state} onChange={(e) => setBiz({ ...biz, state: e.target.value })} placeholder="State" data-testid="rep-input-state" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={biz.category} onChange={(e) => setBiz({ ...biz, category: e.target.value })} placeholder="Category (e.g. Night club)" data-testid="rep-input-category" className="col-span-2 sm:col-span-4 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button onClick={() => setMode("verified")} data-testid="mode-verified"
              className={`rounded-lg px-3 py-1.5 text-[13px] font-medium border ${mode === "verified" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
              Verified reviews (Google)
            </button>
            <button onClick={() => setMode("paste")} data-testid="mode-paste"
              className={`rounded-lg px-3 py-1.5 text-[13px] font-medium border ${mode === "paste" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
              Paste reviews
            </button>
          </div>

          {mode === "verified" ? (
            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3 text-[13px] text-slate-600 flex items-start gap-2" data-testid="verified-note">
              <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              We'll pull a sample of verified reviews from Google Places (when connected). If it isn't connected yet, we'll say so honestly — switch to “Paste reviews” to analyse reviews you provide.
            </div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="paste-list">
              {rows.map((r, i) => <ReviewRow key={i} row={r} i={i} onChange={setRow} onRemove={removeRow} />)}
              <button onClick={() => setRows((rs) => [...rs, { author: "", rating: "", text: "" }])} data-testid="paste-add"
                className="inline-flex items-center gap-1 text-[13px] font-medium text-indigo-600 hover:underline">
                <Plus className="w-4 h-4" /> Add another review
              </button>
            </div>
          )}

          <div className="mt-4">
            <Button onClick={runAnalyze} disabled={loading} data-testid="analyze-button" className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing reviews…</> : <><Star className="w-4 h-4 mr-2" /> Analyse reviews</>}
            </Button>
          </div>
        </div>

        {/* Results */}
        {analysis && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-5" data-testid="reputation-results">
            {/* source note */}
            <div className="flex items-center gap-2 text-[13px] text-slate-500">
              <span>Source: <span className="font-medium text-slate-700">{analysis.source?.source_label || "—"}</span></span>
              <Label label={analysis.source?.verification_status} />
              <span className="text-slate-400">· {analysis.reviews_analyzed} review(s) analysed</span>
            </div>

            {/* per-provider evidence (which providers were queried + what they returned) */}
            {(analysis.source?.sources || []).length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid="review-sources">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Providers queried</p>
                <div className="space-y-1.5">
                  {analysis.source.sources.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px] flex-wrap" data-testid={`review-source-${i}`}>
                      <span className={`w-2 h-2 rounded-full ${s.status === "OK" ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <span className="font-medium text-slate-700">{s.source_label}</span>
                      {s.rating != null && <span className="text-slate-500">{s.rating}★</span>}
                      {s.review_count != null && <span className="text-slate-400">· {s.review_count} reviews</span>}
                      {typeof s.text_reviews === "number" && <span className="text-slate-400">· {s.text_reviews} text</span>}
                      {s.verification_status && <Label label={s.verification_status} />}
                      {s.note && <span className="text-[11px] text-amber-600">{s.note}</span>}
                      {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">source <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.reviews_analyzed === 0 ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-800 flex items-start gap-3" data-testid="reputation-empty">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <p className="text-sm">{analysis.note || analysis.source?.note}</p>
              </div>
            ) : (
              <>
                <RecoveryIndex index={analysis.recovery_index} />

                {/* metrics snapshot */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="reputation-metrics">
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="text-2xl font-bold text-slate-900">{m.average_rating?.value ?? "—"}{m.average_rating?.value ? "★" : ""}</div>
                    <div className="text-[11px] text-slate-500">Average rating</div>
                    <div className="mt-1"><Label label={m.average_rating?.data_label} /></div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="text-2xl font-bold text-slate-900">{m.review_count_analyzed ?? 0}</div>
                    <div className="text-[11px] text-slate-500">Reviews analysed</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="text-2xl font-bold text-red-600">{m.negative_reviews ?? 0}</div>
                    <div className="text-[11px] text-slate-500">Negative (≤2★)</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="text-2xl font-bold text-slate-900">{m.recurring_complaint_count ?? 0}</div>
                    <div className="text-[11px] text-slate-500">Recurring themes</div>
                  </div>
                </div>

                {/* themes */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-slate-400" />
                    <h2 className="font-heading text-lg font-bold text-slate-900">Recurring complaints — fix these first</h2>
                  </div>
                  <div className="space-y-3" data-testid="theme-list">
                    {(analysis.themes || []).map((t, i) => (
                      <ThemeCard key={t.key} theme={t} i={i} added={!!added[t.key]}
                        onAddTask={addTask} onDraft={(e) => setRespCtx(e)} />
                    ))}
                  </div>
                </div>

                {/* policy flags */}
                {(analysis.policy_flags || []).length > 0 && (
                  <div data-testid="policy-flags">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldAlert className="w-4 h-4 text-slate-400" />
                      <h2 className="font-heading text-lg font-bold text-slate-900">Potential policy issues</h2>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-3 text-[12px] text-slate-500">
                      These MAY qualify for a platform's content policy — this is not a guarantee of removal. Review each yourself and, if appropriate, submit it through the platform's official report process. We never remove reviews, offer incentives, or contact reviewers on your behalf.
                    </div>
                    <div className="space-y-2.5">
                      {analysis.policy_flags.map((f, i) => (
                        <div key={i} className="rounded-xl border border-red-200 bg-red-50/60 p-3.5" data-testid={`policy-flag-${i}`}>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Flag className="w-4 h-4 text-red-600" />
                            <span className="font-semibold text-red-700 text-sm">{f.status}</span>
                            <span className="rounded-full border border-red-200 bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-red-600">{f.category}</span>
                            <span className="text-[11px] text-slate-400">confidence {Math.round((f.confidence || 0) * 100)}%</span>
                            <Label label={f.data_label} />
                          </div>
                          <p className="text-[13px] text-slate-600 italic">“{f.text}”</p>
                          <p className="text-[12px] text-slate-600 mt-1.5"><span className="font-medium">Why flagged:</span> {f.reason}</p>
                          <p className="text-[11px] text-slate-400 mt-1">{f.guidance}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* trend */}
                {history.length > 1 && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="reputation-trend">
                    <h3 className="font-semibold text-slate-800 text-sm mb-2">Recovery trend</h3>
                    <div className="flex items-end gap-1.5 h-20">
                      {history.slice(-16).map((h, i) => (
                        <div key={i} className="flex-1 bg-indigo-500/80 rounded-t" style={{ height: `${Math.max(4, (h.recovery_index || 0))}%` }} title={`${h.recovery_index} · ${(h.analyzed_at || "").slice(0, 10)}`} />
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Recovery priority index across {history.length} analyses (higher = more work indicated).</p>
                  </div>
                )}

                {/* legend + fix plan CTA */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {["VERIFIED_SOURCE_DATA", "CUSTOMER_CLAIM", "AI_ANALYZED_THEME", "BUSINESS_RECOMMENDATION"].map((l) => <Label key={l} label={l} />)}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => nav("/tasks")} data-testid="go-fix-plan">
                    <ClipboardList className="w-4 h-4 mr-1.5" /> Open Fix Plan <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>

      {respCtx && <ResponseModal ctx={respCtx} biz={biz} onClose={() => setRespCtx(null)} />}
    </div>
  );
}
